import React, { useEffect, useState } from "react";
import Typeahead from "components/Typeahead";
import { Form } from "react-bootstrap";
import { v4 as genId } from "uuid";
import { LayerFilterQueryOperator, LayerFilterSupportedLayers } from "../types";

const MAX_DISTINCT = 10000;

interface ILayerFilterQueryTextInputParams {
  disabled?: boolean;
  layer?: LayerFilterSupportedLayers;
  field?: __esri.Field;
  prefill?: boolean;
  operator?: LayerFilterQueryOperator;
  value?: string | number;
  onChange?: (value: string) => void;
}

// ✅ More permissive runtime guard: works for FeatureLayer AND sublayers that support querying
function canQueryFeatures(
  l: unknown
): l is {
  createQuery: () => __esri.Query;
  queryFeatures: (q: __esri.Query) => Promise<__esri.FeatureSet>;
} {
  const obj = l as Record<string, unknown> | null;
  return (
    !!obj &&
    typeof obj["createQuery"] === "function" &&
    typeof obj["queryFeatures"] === "function"
  );
}

export default function LayerFilterQueryTextInput({
  disabled,
  layer,
  field,
  operator,
  value: inputValue,
  onChange,
}: ILayerFilterQueryTextInputParams) {
  const [uid] = useState(genId());
  const [typeaheadValue, setTypeaheadValue] = useState<string[]>([]);
  const [value, setValue] = useState<string>("");

  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [hitCap, setHitCap] = useState(false);

  const [prefill, setPrefill] = useState(false);

  useEffect(() => {
    if (typeof inputValue !== "undefined") {
      setValue(inputValue.toString());
      setTypeaheadValue([inputValue.toString()]);
    }
  }, [inputValue]);

  useEffect(() => {
    setPrefill(
      operator
        ? [
            LayerFilterQueryOperator.is,
            LayerFilterQueryOperator.isNot,
          ].includes(operator)
        : false
    );
  }, [operator]);

  useEffect(() => {
    // 🔎 Use the new guard
    if (!prefill || !layer || !field || !canQueryFeatures(layer)) return;

    setLoading(true);
    setHitCap(false);

    const q = layer.createQuery();
    q.outFields = [field.name];
    q.where = "1=1";
    q.returnDistinctValues = true;
    q.orderByFields = [`${field.name} ASC`];
    q.returnGeometry = false;

    // Cap results (resultRecordCount if available, else num)
    if ("resultRecordCount" in q) {
      (q as __esri.Query & {
        resultRecordCount?: number;
      }).resultRecordCount = MAX_DISTINCT;
    } else {
      q.num = MAX_DISTINCT;
    }

    layer
      .queryFeatures(q)
      .then((results) => {
        const vals = results.features
          .map((f) => f.attributes[field.name])
          .filter(
            (v): v is string | number =>
              v !== null && v !== undefined && String(v).trim() !== ""
          )
          .map((v) => String(v));

        const deduped = Array.from(new Set(vals));
        setOptions(deduped);

        // Show warning if server truncated OR we hit the cap in the response size
        const truncated =
          (results as __esri.FeatureSet & { exceededTransferLimit?: boolean })
            .exceededTransferLimit === true ||
          results.features.length >=
            Math.min(MAX_DISTINCT, q.num ?? MAX_DISTINCT);
        if (truncated) setHitCap(true);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [field, layer, prefill]);

  return prefill && layer && field ? (
    <>
      <Typeahead
        id={`layer-filter-query-value-typeahead-${uid}`}
        clearButton
        placeholder="Choose a value"
        disabled={disabled}
        labelKey={(v) => v.toString()}
        isLoading={loading}
        options={options}
        onChange={([option]) => {
          setTypeaheadValue(option ? [option as string] : []);
          onChange?.(`${option ?? ""}`);
        }}
        selected={typeaheadValue}
      />
      {hitCap && (
        <small className="text-white">
          Showing the first {MAX_DISTINCT.toLocaleString()} values. Refine your
          search to narrow results.
        </small>
      )}
    </>
  ) : (
    <Form.Control
      type="text"
      required
      placeholder="Value"
      disabled={disabled}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        onChange?.(e.target.value);
      }}
    />
  );
}
